/**
 * Reads GA4's client ID so a server-side conversion can be attributed to
 * the same visitor as their browsing.
 *
 * ## Why not FPID
 *
 * Our sGTM container manages cookies server-side and sets `FPID` — durable
 * for two years and immune to Safari's ITP cap, which is exactly why it
 * exists. It's also `HttpOnly`, and this app is a pure client-side SPA, so
 * there is no server layer here that could read it on our behalf.
 *
 * `_ga` is the readable one. gtag writes it client-side, so Safari caps it
 * at 7 days — which matters less than it sounds: this value is needed at
 * the moment someone clicks through to checkout, in the same session they
 * were already browsing in. The 90-day problem belongs to attribution, and
 * that's solved separately by cookies set from the marketing site's
 * middleware as response headers.
 *
 * Without this, conversions reported from pref-storage arrive under a
 * synthetic client ID derived from the Chargebee customer, so GA4 sees a
 * brand-new user with no history rather than the visitor who clicked the ad.
 *
 * This module is intentionally duplicated in the marketing repo
 * (marketing-and-news-app). Keep them in sync.
 */

const GA_COOKIE = "_ga";

/** `GA1.1.303046579.1748962260` -> `303046579.1748962260` */
export function parseGaClientId(raw: string | undefined | null): string | null {
  if (!raw) return null;
  // GA1.<depth>.<id>.<timestamp>; the client ID is the trailing two parts,
  // and `depth` varies by cookie domain so it can't be indexed from the front.
  const parts = raw.split(".");
  if (parts.length < 4) return null;
  const clientId = parts.slice(-2).join(".");
  // Anything else means a format we don't recognise. Sending a malformed
  // client ID is worse than sending none: GA4 accepts it and silently
  // attributes the conversion to a user who doesn't exist.
  return /^\d+\.\d+$/.test(clientId) ? clientId : null;
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * The visitor's GA4 client ID, or null when GTM hasn't set one yet.
 */
export function getGaClientId(): string | null {
  if (typeof document === "undefined") return null;
  return parseGaClientId(readCookie(GA_COOKIE));
}
